import { Outlet, Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Menu, Moon, SunMedium } from 'lucide-react';
import { AnnouncementBar } from '@kollez/design-system';

/**
 * PublicShell (F80) — ported from kollez/assets/public.js.
 * Structure: .pub-h header (logo + nav + Sign in / Get started + theme + hamburger)
 *            <main>
 *            .pub-f footer (4-column grid + copyright bar)
 */

/**
 * Kollez wordmark from apps/web/public/brand/ (Jana's brand pack, PR #768).
 * Two theme variants; the wrong one is hidden by CSS depending on
 * data-theme on <html>. Both files include the mark + wordmark + tagline
 * so no inline text/hex is needed at this call site.
 */
function Logo() {
  return (
    <>
      <img
        src="/brand/logo-wordmark-light.svg"
        alt="Kollez"
        className="pub-logo pub-logo-light"
        style={{ height: 56, width: 'auto' }}
      />
      <img
        src="/brand/logo-wordmark-dark.svg"
        alt="Kollez"
        className="pub-logo pub-logo-dark"
        style={{ height: 56, width: 'auto', display: 'none' }}
      />
    </>
  );
}

const NAV: Array<[string, string, string]> = [
  ['platform', 'Platform', '/'],
  ['nexie', 'Nexie', '/ai'],
  ['assessments', 'Assessments', '/assessments'],
  ['institutions', 'For institutions', '/for-institutions'],
  ['partners', 'For partners', '/for-partners'],
  ['students', 'For students', '/for-students'],
  ['pricing', 'Pricing', '/pricing'],
  ['security', 'Security', '/security'],
];

// Every footer link points at a real, live destination. Legacy `/#nexie`,
// `/#about`, `/security#terms`, `/security#privacy`, `/security#status`
// anchors have been removed or repointed — none of those anchors exist
// on their target pages after the homepage narrow (PR #13).
const FOOTER_COLS: Array<[string, Array<[string, string]>]> = [
  ['Product', [
    ['Nexie AI', '/ai'],
    ['Assessments', '/assessments'],
    ['For institutions', '/for-institutions'],
    ['For partners', '/for-partners'],
    ['For students', '/for-students'],
    ['Pricing', '/pricing'],
    ['Customers', '/customers'],
  ]],
  ['Get started', [
    ['Create an account', '/register'],
    ['Start a free pilot', '/register/institution?plan=pilot'],
    ['Get a quote', '/pricing'],
    ['Book a demo', '/contact'],
    ['Accept an invitation', '/accept-invite'],
  ]],
  ['Trust', [
    ['Security and privacy', '/security'],
    ['DPDP Act notice', '/security#dpdp'],
    ['Status', '/status'],
    ['Report a fake job', '/contact?topic=fraud'],
  ]],
  ['Company', [
    ['About us', '/about'],
    ['Changelog', '/changelog'],
    ['Contact', '/contact'],
    ['Terms', '/terms'],
    ['Privacy policy', '/privacy'],
  ]],
];

function useTheme(): [string, (t: string) => void] {
  const [t, setT] = useState<string>(() => {
    try { return localStorage.getItem('kollez-theme') || 'ruled'; } catch { return 'ruled'; }
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', t);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', t === 'blackboard' ? '#1F40AF' : '#FFF8EA');
    try { localStorage.setItem('kollez-theme', t); } catch {}
  }, [t]);
  return [t, setT];
}

export function PublicShell() {
  const [theme, setTheme] = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const loc = useLocation();
  const activeKey =
    loc.pathname === '/' ? 'platform'
    : loc.pathname === '/ai' || loc.pathname.startsWith('/ai/') ? 'nexie'
    : loc.pathname.startsWith('/assessments') ? 'assessments'
    : loc.pathname.startsWith('/for-institutions') ? 'institutions'
    : loc.pathname.startsWith('/for-partners') || loc.pathname.startsWith('/for-companies') ? 'partners'
    : loc.pathname.startsWith('/for-students') ? 'students'
    : loc.pathname.startsWith('/pricing') ? 'pricing'
    : loc.pathname.startsWith('/security') ? 'security'
    : '';

  return (
    <>
      <a href="#main" className="skip">Skip to content</a>

      <AnnouncementBar
        id="season-2026-27"
        message="Placement season 2026–27 is live across partner campuses."
        href="/contact?topic=demo"
        linkLabel="Book a 30-min demo"
      />

      <header className="pub-h">
        <div className="pub-in">
          <Link to="/" className="flex items-center gap-2" aria-label="kollez.ai home">
            <Logo />
          </Link>
          <nav className="pub-nav" aria-label="Main">
            {NAV.map((n) => (
              <Link key={n[0]} className="btn btn-ghost btn-sm" to={n[2]} aria-current={n[0] === activeKey ? 'page' : undefined}>
                {n[1]}
              </Link>
            ))}
          </nav>
          <div className="pub-act">
            <button
              className="btn btn-ghost btn-icon btn-sm"
              aria-label="Switch light or dark"
              onClick={() => setTheme(theme === 'blackboard' ? 'ruled' : 'blackboard')}
            >
              {theme === 'blackboard' ? <SunMedium size={16} /> : <Moon size={16} />}
            </button>
            <Link className="btn btn-ghost btn-sm pub-hide-sm" to="/login">Sign in</Link>
            <Link className="btn btn-primary btn-sm" to="/register">Get started</Link>
            <button
              className="btn btn-ghost btn-icon btn-sm pub-menu-btn"
              aria-label="Menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <Menu size={16} />
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="pub-menu">
            {NAV.map((n) => (
              <Link key={n[0]} to={n[2]} onClick={() => setMenuOpen(false)} aria-current={n[0] === activeKey ? 'page' : undefined}>{n[1]}</Link>
            ))}
            <Link to="/contact" onClick={() => setMenuOpen(false)}>Book a demo</Link>
            <Link to="/login" onClick={() => setMenuOpen(false)}>Sign in</Link>
          </div>
        )}
      </header>

      <main id="main">
        <Outlet />
      </main>

      <footer className="pub-f">
        <div className="pub-f-in">
          <div className="pub-f-brand">
            <Link to="/" className="flex items-center gap-2"><Logo /></Link>
            <p className="helper m-0">Campus placements for Indian colleges and education groups. Made in Hyderabad.</p>
            <p className="helper m-0">
              Students never pay. If anyone asks you for a fee to get a job through kollez,{' '}
              <Link className="link" to="/contact?topic=fraud">tell us</Link>.
            </p>
          </div>
          {FOOTER_COLS.map(([title, links]) => (
            <div key={title}>
              <h3 className="pub-f-h">{title}</h3>
              <ul>
                {links.map(([label, href]) => (
                  <li key={label + href}>
                    <Link to={href}>{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="pub-f-bar">
          <span>© {new Date().getFullYear()} Sonido Labs Pvt. Ltd. · CIN on request</span>
          <span>Made in Hyderabad · Priced in INR</span>
        </div>
      </footer>
    </>
  );
}

/**
 * AuthShell (F80) — slim header for auth flows (login / register / verify / forgot-password).
 * Body content is centered and free of nav — matches kollez/pages/public/login.html.
 */
export function AuthShell({ alt }: { alt?: 'signin' | 'signup' | 'none' }) {
  const [theme, setTheme] = useTheme();
  const right = alt === 'signup'
    ? (<><span className="text-sm text-ink-tertiary pub-hide-sm">New here?</span><Link className="btn btn-outline btn-sm" to="/register">Create account</Link></>)
    : alt === 'signin'
    ? (<><span className="text-sm text-ink-tertiary pub-hide-sm">Already registered?</span><Link className="btn btn-outline btn-sm" to="/login">Sign in</Link></>)
    : null;
  return (
    <>
      <a href="#main" className="skip">Skip to content</a>
      <header className="pub-h pub-h-auth">
        <div className="pub-in">
          <Link to="/" className="flex items-center gap-2" aria-label="kollez.ai home"><Logo /></Link>
          <div className="pub-act">
            <button
              className="btn btn-ghost btn-icon btn-sm"
              aria-label="Switch light or dark"
              onClick={() => setTheme(theme === 'blackboard' ? 'ruled' : 'blackboard')}
            >
              {theme === 'blackboard' ? <SunMedium size={16} /> : <Moon size={16} />}
            </button>
            {right}
          </div>
        </div>
      </header>
      <main id="main">
        <Outlet />
      </main>
    </>
  );
}
